/**
 * PMGT FE-2 — CREATE-mode orchestrator for the unified `ProjectWizard`.
 *
 * This is the original `CreateProjectPageInner`, refactored to render through
 * the shared `WizardShell` + shared step bodies. Behaviour is IDENTICAL to the
 * shipped FE-1 create flow: lifted draft state, holistic validation (FB-08),
 * and the mint-on-submit hierarchy:
 *
 *   `POST /projects`
 *     → per park: find-or-create site → `POST /contracts` (park = Contract)
 *     → per turbine: find-or-create asset → `POST /contract-positions`
 *       (turbine = Contract Position)
 *
 * The create tests (`CreateProjectPage.test.tsx`) drive THIS component via the
 * thin `CreateProjectPage` wrapper, so the mocked hook surface is unchanged.
 *
 * M3C-04 — workflow first (DI-D62 c1): step 0 is `PickWorkflowStep` above the
 * stepper (one own workflow → preselected, one line; several → the default
 * preselected; none → the create is blocked and names the admin). The picked
 * version's `node.project_create` → `ui.fields` decides each field
 * (`fieldRules.ts`): hidden fields are neither rendered nor validated nor
 * sent; prefilled parties render read-only "set by the workflow" and their
 * value is sent; the request carries `workflowVersionId` (projects/v1
 * 2.26.0). A workflow whose start node is not one this release mounts as
 * the wizard is not a candidate (`projectWorkflows`).
 */
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useHasRole } from "@aeriosiq/auth-client";
import { useToast } from "../../account/ToastContext.js";
import { useCreateProjectMutation } from "../useProjects.js";
import { useCreateContractMutation } from "../../contracts/useContracts.js";
import { useCreateContractPositionMutation } from "../../contract-positions/useContractPositions.js";
import { useKnownTenantsQuery } from "../access/accessHooks.js";
import { useTenantSettingsQuery } from "../../tenancy/hooks.js";
import type { CreateProjectRequest } from "../types.js";
import { useCreateSiteMutation } from "../../sites/queries/hooks.js";
import type { CreateSiteBody } from "../../sites/queries/types.js";
import {
  useCreateTurbineMutation,
  type CreateTurbineBody,
} from "../../turbines/queries/index.js";
import { ImportTurbinesWizard } from "../import/ImportTurbinesWizard.js";
import { WizardShell } from "./WizardShell.js";
import { BasicsStep, PartiesStep } from "./WizardSteps.js";
import {
  projectWorkflows,
  useWorkflows,
  workflowByVersionId,
} from "../../workflows/api/workflows.js";
import {
  PickWorkflowStep,
  preselectedOption,
  workflowOptionsOf,
} from "../../workflows/PickWorkflowStep.js";
import { nodeForModule } from "../../workflows/moduleRegistry.js";
import { workflowErrorMessage } from "../WorkflowSelect.js";
import { isHidden, isPrefilled, readFieldRules } from "./fieldRules.js";
import {
  EMPTY_CONTRACT_PARTY,
  EMPTY_PARTY,
  EMPTY_REPORTING,
  ParkCard,
  STEP_LABELS,
  hasAnyError,
  nextKey,
  stepsWithErrors,
  validateWizard,
  type ContractPartyValue,
  type DraftPark,
  type DraftTurbine,
  type PartyValue,
  type ReportingValue,
} from "./wizardParts.js";
import { FileUpIcon, PlusIcon } from "@aeriosiq/ui/icons";

// FB-16b — lazy-load the create Review map (MapLibre + PMTiles) — mirrors the
// edit Overview map so the heavy GL libs stay out of the eager bundle.
const CreateReviewMap = lazy(() =>
  import("./CreateReviewMap.js").then((m) => ({ default: m.CreateReviewMap })),
);

export function CreateModeWizard(): ReactElement {
  const navigate = useNavigate();
  const { toast } = useToast();
  // SB2-CC-01 AC9 — route the role-identity check through the canonical helper
  // instead of comparing the role inline. `useHasRole` does the comparison
  // inside the allowlisted `useCurrentUserRole.ts`, so "who may share a project"
  // stays defined in one place rather than being copied into this component.
  // Gates an informational hint only; the BE is the security boundary.
  const isTenantAdmin = useHasRole("tenant_admin");

  const knownTenantsQuery = useKnownTenantsQuery();
  const knownTenants = knownTenantsQuery.data?.items ?? [];
  const knownTenantsLoading = knownTenantsQuery.isLoading;

  const createProject = useCreateProjectMutation();
  const createSite = useCreateSiteMutation();
  const createTurbine = useCreateTurbineMutation();
  // FE-1 — the hierarchy builders (park → Contract, turbine → Contract Position).
  const createContract = useCreateContractMutation();
  const createContractPosition = useCreateContractPositionMutation();

  const [activeStep, setActiveStep] = useState(0);
  const [validated, setValidated] = useState(false);

  const [projectName, setProjectName] = useState("");
  const [offerReference, setOfferReference] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportLanguage, setReportLanguage] = useState("");
  const [aiModelIds, setAiModelIds] = useState<string[]>([]);
  // M3C-04 — step 0: the workflow VERSION the project starts with (own
  // workflows whose start node mounts this wizard; workflows/v1). The only
  // one, else the tenant's default, is preselected once the list arrives;
  // the user's choice is never overwritten.
  const workflows = useWorkflows();
  const candidates = useMemo(
    () => projectWorkflows(workflows.data),
    [workflows.data],
  );
  const options = useMemo(() => workflowOptionsOf(candidates), [candidates]);
  const [workflowVersionId, setWorkflowVersionId] = useState("");
  const [workflowTouched, setWorkflowTouched] = useState(false);
  useEffect(() => {
    if (workflowTouched || workflowVersionId !== "") return;
    const seeded = preselectedOption(options);
    if (seeded) setWorkflowVersionId(seeded);
  }, [options, workflowTouched, workflowVersionId]);
  const picked = workflowByVersionId(candidates, workflowVersionId);
  const workflowMissing = picked === null;
  // the field layer: the picked version's project_create node (DI-D62 c1)
  const startNode = nodeForModule(picked?.latestVersion, "projects.wizard");
  const rules = useMemo(
    () => readFieldRules(startNode?.ui.fields ?? null),
    [startNode],
  );
  const tenantSettings = useTenantSettingsQuery();
  const maxAiModels = tenantSettings.data?.maxAiModelsPerProject ?? null;

  const [customer, setCustomer] = useState<PartyValue>(EMPTY_PARTY);
  const [contractor, setContractor] = useState<PartyValue>(EMPTY_PARTY);
  const [reporting, setReporting] = useState<ReportingValue>(EMPTY_REPORTING);

  // FB-18 / FB-43 — project-wide DEFAULTS for the contract-scoped parties
  // (step 2), each a tenant|contact party value. Each park inherits these
  // unless it overrides them. tenant_admin-gated.
  const [contractContractorDefault, setContractContractorDefault] =
    useState<ContractPartyValue>(EMPTY_CONTRACT_PARTY);
  const [contractCustomerDefault, setContractCustomerDefault] =
    useState<ContractPartyValue>(EMPTY_CONTRACT_PARTY);

  const [parks, setParks] = useState<DraftPark[]>([]);
  const [turbines, setTurbines] = useState<DraftTurbine[]>([]);

  const [importOpen, setImportOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const busy =
    createProject.isPending ||
    createSite.isPending ||
    createTurbine.isPending ||
    createContract.isPending ||
    createContractPosition.isPending;

  const trimmedName = projectName.trim();

  const basics = useMemo(
    () => ({ offerReference, reportLanguage, startDate, endDate, aiModelIds }),
    [offerReference, reportLanguage, startDate, endDate, aiModelIds],
  );
  const errors = useMemo(
    () =>
      validateWizard({
        trimmedName,
        customer,
        contractor,
        reporting,
        parks,
        turbines,
        rules,
        basics,
      }),
    [
      trimmedName,
      customer,
      contractor,
      reporting,
      parks,
      turbines,
      rules,
      basics,
    ],
  );
  const showErrors = validated;
  const errorSteps = useMemo(() => {
    if (!showErrors) return new Set<number>();
    const steps = stepsWithErrors(errors);
    // PM-WF-01 — the required workflow lives on the Basics step (0).
    if (workflowMissing) steps.add(0);
    return steps;
  }, [showErrors, errors, workflowMissing]);

  function addPark(): void {
    setParks((prev) => [
      ...prev,
      {
        key: nextKey("park"),
        siteName: "",
        countryCode: "",
        isOffshore: false,
      },
    ]);
  }
  function updatePark(key: string, patch: Partial<DraftPark>): void {
    setParks((prev) =>
      prev.map((p) => (p.key === key ? { ...p, ...patch } : p)),
    );
  }
  function removePark(key: string): void {
    setParks((prev) => prev.filter((p) => p.key !== key));
    // FB-17 — turbines live UNDER their park card now; removing the park
    // removes its turbines (no orphaned, park-less rows).
    setTurbines((prev) => prev.filter((t) => t.parkKey !== key));
  }

  function updateTurbine(next: DraftTurbine): void {
    setTurbines((prev) => prev.map((t) => (t.key === next.key ? next : t)));
  }
  function removeTurbine(key: string): void {
    setTurbines((prev) => prev.filter((t) => t.key !== key));
  }
  // FB-17 — add a NEW (find-or-create) turbine row scoped to a specific park.
  function addTurbineForPark(parkKey: string): void {
    setTurbines((prev) => [
      ...prev,
      {
        key: nextKey("turbine"),
        parkKey,
        identifier: "",
        internalParkNumber: "",
        assetTypeId: null,
        assetModelId: null,
        operatorMode: "none",
        operatorTenantId: null,
        operatorContactId: null,
      },
    ]);
  }
  // FB-17 — replace a park's EXISTING (bulk-added) turbines with the given set
  // while leaving its NEW (manually-added) rows untouched. Drives the
  // one-click bulk add + idempotent re-resolve.
  function bulkSetExistingForPark(
    parkKey: string,
    existingDrafts: readonly DraftTurbine[],
  ): void {
    setTurbines((prev) => [
      ...prev.filter(
        (t) =>
          t.parkKey !== parkKey ||
          // keep manually-added (non-existing) rows of this park
          t.existingAssetId === undefined,
      ),
      ...existingDrafts,
    ]);
  }

  function buildProjectBody(): CreateProjectRequest {
    // M3C-04 — the version picked at step 0 (projects/v1 2.26.0); the server
    // stores its preset key beside it (dual-write, M3C-03).
    const body: CreateProjectRequest = { projectName: trimmedName };
    if (picked) body.workflowVersionId = picked.latestVersion.versionId;
    // a field the workflow PREFILLS is sent with the version's value; a
    // HIDDEN field is never sent (M3C-04 field layer)
    const partyBody = (
      party: "customer" | "contractor",
      v: PartyValue,
    ): void => {
      const t = rules[`${party}TenantId`];
      const c = rules[`${party}ContactId`];
      if (isPrefilled(t)) {
        if (t.value) body[`${party}TenantId`] = t.value;
        return;
      }
      if (isPrefilled(c)) {
        if (c.value) body[`${party}ContactId`] = c.value;
        return;
      }
      // CM-9 — tenant mode → `*TenantId` (auto-grants, XT-D23); contact mode →
      // `*ContactId` (descriptive only, never grants, CM-D13). At most one of
      // the pair is sent per party.
      if (v.mode === "tenant" && v.tenantId && !isHidden(t)) {
        body[`${party}TenantId`] = v.tenantId;
      } else if (v.mode === "contact" && v.contactId && !isHidden(c)) {
        body[`${party}ContactId`] = v.contactId;
      }
    };
    partyBody("customer", customer);
    partyBody("contractor", contractor);
    const rep = rules.reportingSubcontractorTenantId;
    if (isPrefilled(rep)) {
      if (rep.value) body.reportingSubcontractorTenantId = rep.value;
    } else if (
      !isHidden(rep) &&
      reporting.choice === "partner" &&
      reporting.tenantId
    ) {
      body.reportingSubcontractorTenantId = reporting.tenantId;
    }
    const text = (
      f: "offerReference" | "reportLanguage" | "startDate" | "endDate",
      v: string,
    ): void => {
      const r = rules[f];
      if (isHidden(r)) return;
      const value = isPrefilled(r) ? (r.value ?? "") : v.trim();
      if (value.length > 0) body[f] = value;
    };
    text("offerReference", offerReference);
    text("startDate", startDate);
    text("endDate", endDate);
    text("reportLanguage", reportLanguage);
    if (!isHidden(rules.aiModelIds) && aiModelIds.length > 0)
      body.aiModelIds = [...aiModelIds];
    return body;
  }

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    setValidated(true);
    const allErrors = validateWizard({
      trimmedName,
      customer,
      contractor,
      reporting,
      parks,
      turbines,
      rules,
      basics,
    });
    if (hasAnyError(allErrors) || workflowMissing) {
      const flagged = stepsWithErrors(allErrors);
      if (workflowMissing) flagged.add(0);
      const firstBad = [0, 1, 2].find((s) => flagged.has(s));
      if (firstBad !== undefined) setActiveStep(firstBad);
      setSubmitError(
        "Please fix the highlighted fields before creating the project.",
      );
      return;
    }

    try {
      const project = await createProject.mutateAsync(buildProjectBody());

      const siteIdByKey = new Map<string, string>();
      for (const park of parks) {
        if (park.existingSiteId !== undefined) {
          siteIdByKey.set(park.key, park.existingSiteId);
          continue;
        }
        const siteBody: CreateSiteBody = { siteName: park.siteName.trim() };
        if (park.countryCode.trim().length > 0) {
          siteBody.countryCode = park.countryCode.trim().toUpperCase();
        }
        if (park.isOffshore) siteBody.isOffshore = true;
        const site = await createSite.mutateAsync(siteBody);
        siteIdByKey.set(park.key, site.id);
      }

      const contractIdByParkKey = new Map<string, string>();
      for (const park of parks) {
        const siteId = siteIdByKey.get(park.key);
        if (siteId === undefined) continue;
        // FB-18 / FB-43 — resolve the park's contract parties: its own override
        // else the step-2 project-wide default. Each is a tenant|contact value;
        // tenant mode → `*TenantId` (auto-grants the contract-scoped role via
        // `reconcilePartyGrants`, tenant_admin-gated, PM-D23), contact mode →
        // `*ContactId` (descriptive only, never grants). Mutually exclusive.
        const contractContractor =
          park.contractContractorOverridden === true
            ? (park.contractContractor ?? EMPTY_CONTRACT_PARTY)
            : contractContractorDefault;
        const contractCustomer =
          park.contractCustomerOverridden === true
            ? (park.contractCustomer ?? EMPTY_CONTRACT_PARTY)
            : contractCustomerDefault;
        const contract = await createContract.mutateAsync({
          projectId: project.projectId,
          siteId,
          contractorTenantId:
            contractContractor.mode === "tenant"
              ? contractContractor.tenantId
              : null,
          contractorContactId:
            contractContractor.mode === "contact"
              ? contractContractor.contactId
              : null,
          customerTenantId:
            contractCustomer.mode === "tenant"
              ? contractCustomer.tenantId
              : null,
          customerContactId:
            contractCustomer.mode === "contact"
              ? contractCustomer.contactId
              : null,
        });
        contractIdByParkKey.set(park.key, contract.contractId);
      }

      for (const turbine of turbines) {
        const contractId = contractIdByParkKey.get(turbine.parkKey);
        if (contractId === undefined) {
          continue;
        }

        let assetId = turbine.existingAssetId;
        if (assetId === undefined) {
          const siteId = siteIdByKey.get(turbine.parkKey);
          if (
            siteId === undefined ||
            turbine.assetTypeId === null ||
            turbine.assetModelId === null
          ) {
            continue;
          }
          const turbineBody: CreateTurbineBody = {
            siteId,
            assetTypeId: turbine.assetTypeId,
            assetModelId: turbine.assetModelId,
            identifier: turbine.identifier.trim(),
          };
          if (turbine.internalParkNumber.trim().length > 0) {
            turbineBody.internalParkNumber = turbine.internalParkNumber.trim();
          }
          // CM-9 — operator is polymorphic + descriptive only (never grants,
          // PM-D22): tenant mode → `operatorTenantId`, contact mode →
          // `operatorContactId`.
          if (turbine.operatorMode === "tenant" && turbine.operatorTenantId) {
            turbineBody.operatorTenantId = turbine.operatorTenantId;
          } else if (
            turbine.operatorMode === "contact" &&
            turbine.operatorContactId
          ) {
            turbineBody.operatorContactId = turbine.operatorContactId;
          }
          const asset = await createTurbine.mutateAsync(turbineBody);
          assetId = asset.id;
        }

        await createContractPosition.mutateAsync({ contractId, assetId });
      }

      toast(`Project created (code ${String(project.projectCode)}).`);
      void navigate("/projects");
    } catch (err) {
      const status = (err as { status?: number }).status;
      const body = (err as { body?: { detail?: string; message?: string } })
        .body;
      const detail = body?.detail ?? body?.message ?? null;
      // PM-WF-01 — the BE refused the workflow (422 unknown / not available).
      const reason = (err as { body?: { reason?: unknown } }).body?.reason;
      if (
        status === 422 &&
        (reason === "unknown_workflow" || reason === "workflow_not_available")
      ) {
        setActiveStep(0);
        setSubmitError(workflowErrorMessage(err));
        return;
      }
      // FB-18 — a 403 on a contract create = binding a contract party without
      // the owner `tenant_admin` role (the BE asserts this, PM-D23). Surface a
      // friendly party-bind message instead of the generic create error.
      if (status === 403) {
        setSubmitError(
          detail !== null && detail.length > 0
            ? detail
            : "You don't have permission to bind a contract party. Binding a contract contractor or customer requires a tenant administrator.",
        );
        return;
      }
      setSubmitError(
        detail !== null && detail.length > 0
          ? detail
          : `Could not create the project${
              typeof status === "number" ? ` (HTTP ${String(status)})` : ""
            }. Please try again.`,
      );
    }
  }

  const isLastStep = activeStep === STEP_LABELS.length - 1;
  // M3C-04 — no workflow to start a project with (the candidates are the own
  // workflows whose start node mounts THIS wizard): the create is blocked and
  // the step names the admin; the form is not offered.
  const blocked = workflows.isSuccess && options.length === 0;

  function renderStep(): ReactElement | null {
    if (activeStep === 0) {
      return (
        <Stack spacing={3}>
          <BasicsStep
            projectName={projectName}
            onProjectName={setProjectName}
            offerReference={offerReference}
            onOfferReference={setOfferReference}
            reportLanguage={reportLanguage}
            onReportLanguage={setReportLanguage}
            startDate={startDate}
            onStartDate={setStartDate}
            endDate={endDate}
            onEndDate={setEndDate}
            aiModelIds={aiModelIds}
            onAiModelIds={setAiModelIds}
            maxAiModels={maxAiModels}
            showErrors={showErrors}
            errors={errors}
            rules={rules}
          />
        </Stack>
      );
    }
    if (activeStep === 1) {
      return (
        <PartiesStep
          customer={customer}
          onCustomer={setCustomer}
          contractor={contractor}
          onContractor={setContractor}
          reporting={reporting}
          onReporting={setReporting}
          knownTenants={knownTenants}
          knownTenantsLoading={knownTenantsLoading}
          showErrors={showErrors}
          errors={errors}
          contractContractorDefault={contractContractorDefault}
          onContractContractorDefault={setContractContractorDefault}
          contractCustomerDefault={contractCustomerDefault}
          onContractCustomerDefault={setContractCustomerDefault}
          rules={rules}
        />
      );
    }
    if (activeStep === 2) {
      return (
        <Box component="section" aria-label="Parks and turbines">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6">Parks &amp; Turbines</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileUpIcon fontSize="small" />}
              aria-label="Import from Excel or CSV"
              onClick={() => {
                setImportOpen(true);
              }}
            >
              Import from Excel or CSV
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Add a park, then its turbines. Picking a park from the catalogue
            adds all of its turbines at once — deselect any you don&apos;t need.
            Or import them in bulk.
          </Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            {parks.map((park) => (
              <ParkCard
                key={park.key}
                park={park}
                parkError={errors.parks[park.key]}
                turbines={turbines.filter((t) => t.parkKey === park.key)}
                turbineErrors={errors.turbines}
                knownTenants={knownTenants}
                knownTenantsLoading={knownTenantsLoading}
                contractContractorDefault={contractContractorDefault}
                contractCustomerDefault={contractCustomerDefault}
                showErrors={showErrors}
                onChangePark={(patch) => {
                  updatePark(park.key, patch);
                }}
                onRemovePark={() => {
                  removePark(park.key);
                }}
                onAddTurbine={() => {
                  addTurbineForPark(park.key);
                }}
                onUpdateTurbine={updateTurbine}
                onRemoveTurbine={removeTurbine}
                onBulkSetExisting={(drafts) => {
                  bulkSetExistingForPark(park.key, drafts);
                }}
              />
            ))}
            <Box>
              <Button
                size="small"
                startIcon={<PlusIcon fontSize="small" />}
                onClick={addPark}
              >
                Add park
              </Button>
            </Box>
          </Stack>
        </Box>
      );
    }
    // Step 4 — Review & create (draft summary).
    return (
      <Box component="section" aria-label="Review and create">
        <Typography variant="h6" gutterBottom>
          Review &amp; create
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>Project:</strong>{" "}
            {trimmedName.length > 0 ? trimmedName : "(no name yet)"}
          </Typography>
          <Typography variant="body2">
            <strong>Parks:</strong> {parks.length} · <strong>Turbines:</strong>{" "}
            {turbines.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click <strong>Create project</strong> to validate every step and
            create the project, its parks, and its turbines.
          </Typography>
        </Stack>

        {/* FB-16b — map of the added turbines that have a location. Existing
            turbines (bulk-added or picked) carry coords from the catalogue;
            newly typed-in turbines have no location yet (noted in the overlay).
            Reuses the same MapLibre core as the edit Overview map. */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Turbine locations
          </Typography>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            }
          >
            <CreateReviewMap turbines={turbines} />
          </Suspense>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        Set up the project base, its parties, and attach parks &amp; turbines.
      </Typography>

      {/* M3C-04 — step 0: pick a workflow (DI-D62 c1). */}
      <Box component="section" aria-label="Workflow" sx={{ mt: 2 }}>
        {workflows.isPending && workflows.fetchStatus !== "idle" ? (
          <CircularProgress size={20} aria-label="Loading workflows" />
        ) : workflows.isError ? (
          <Alert severity="error" variant="outlined">
            Couldn&apos;t load the workflows your company can use.
          </Alert>
        ) : (
          <PickWorkflowStep
            entry="project"
            options={options}
            value={workflowVersionId || null}
            onChange={(id) => {
              setWorkflowTouched(true);
              setWorkflowVersionId(id);
            }}
            disabled={busy}
          />
        )}
        {showErrors && workflowMissing && options.length > 0 ? (
          <Typography variant="caption" color="error">
            Choose the workflow the project starts with.
          </Typography>
        ) : null}
      </Box>

      {!isTenantAdmin && knownTenants.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          Sharing a project with a partner company is a tenant-administrator
          action. You can still create the project and add parks &amp; turbines.
        </Alert>
      ) : null}

      {blocked ? null : (
        <WizardShell
          stepLabels={STEP_LABELS}
          activeStep={activeStep}
          onStepChange={setActiveStep}
          errorSteps={errorSteps}
          showErrorBanner={showErrors && hasAnyError(errors)}
          submitError={submitError}
          primaryLabel="Create project"
          showPrimaryAction={isLastStep}
          primaryDisabled={busy}
          onPrimary={() => {
            void onSubmit();
          }}
          onBack={() => {
            setActiveStep((s) => Math.max(0, s - 1));
          }}
          onNext={() => {
            setActiveStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
          }}
          onCancel={() => {
            void navigate("/projects");
          }}
          showNext={!isLastStep}
        >
          {renderStep()}
        </WizardShell>
      )}

      <ImportTurbinesWizard
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        commitContext={{
          project: buildProjectBody(),
          ...(picked ? { projectWorkflowLabel: picked.name } : {}),
        }}
        onCommitted={() => {
          toast("Turbines imported.");
          void navigate("/projects");
        }}
      />
    </Box>
  );
}
